import { MDBAlert, MDBBtn, MDBDatatable, MDBIcon, MDBInput } from "mdb-react-ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMisc, getVMBoxUsers, updateSharedVoicemail } from "../apiclients/streamsapi_bridge";
import AddToUsers from "./popups/misc/AddToUsers";
import RemoveToUser from "./popups/misc/RemoveToUser";
import PopupLoaderNew from "./popups/PopupLoaderNew";
import TopMessage from "../components/header/TopMessage";
import TopSuccess from "../components/header/TopSuccess";
import { customSearch, useAutoFocusSearchAfterLoads } from "../components/CustomSearchUtil/CustomSearch";
import UIConstant from "../apiclients/UIConstant";

function VMSettingsUsers({ vmid }) {
    const { t } = useTranslation();
    const siteid = JSON.parse(sessionStorage.getItem("admin_session")).siteid;

    const [addUser, setAddUser] = useState(false);
    const toggleOpen = () => setAddUser(!addUser);
    const [userList, setUserList] = useState([]);
    const [sourceUsers, setSourceUsers] = useState([]);
    const [updateUsers, setUpdateUsers] = useState([]);
    const [isAddedUsers, setIsAddedUsers] = useState(false);
    const [isdelete, setIsDelete] = useState(false);
    const dTogglePopup = () => setIsDelete(!isdelete);
    const [isdeleteUser, setIsDeleteUser] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [internalError, setInternalError] = useState(false);
    const addUserRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [filteredRows, setFilteredRows] = useState([]);
    const [vmUserDetails, setVmUserDetails] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const searchRef = useRef(null);
    useAutoFocusSearchAfterLoads(dataLoaded, setDataLoaded, searchRef, true);

    function handleClick() {
        addUserRef.current.click();
    }

    useEffect(() => {
        if (successMessage) {
            setTimeout(() => setSuccessMessage(""), 5000);
        }
    }, [successMessage]);

    // Fetch assigned VM users — enriched with location_name from sourceUsers once available
    useEffect(() => {
        async function fetchVMUsers() {
            try {
                const response = await getVMBoxUsers(siteid, vmid);
                if (response.status === 200) {
                    const data = await response.json();
                    console.log("VMSettingsUsers fetchVMUsers ::: " + JSON.stringify(data));
                    const normalized = (data?.vm_users ?? []).map((u) => ({ ...u, name: u.username ?? u.name }));
                    setUserList(normalized);
                }
            } catch (error) {
                console.error("Error fetching VM users:", error);
            }
        }
        if (vmid && siteid) {
            fetchVMUsers();
        }
    }, [vmid, siteid]);

    // Once sourceUsers loads, enrich userList entries with location_name
    useEffect(() => {
        if (sourceUsers.length === 0 || userList.length === 0) return;
        const sourceMap = new Map(sourceUsers.map((u) => [u.agentid, u]));
        setUserList((prev) =>
            prev.map((u) => {
                const src = sourceMap.get(u.agentid);
                return src ? { ...u, location_name: src.location_name ?? u.location_name } : u;
            })
        );
    }, [sourceUsers]);

    // Fetch source users for AddToUsers popup via getMisc
    useEffect(() => {
        async function fetchSourceUsers() {
            try {
                const apiResponse = await getMisc(siteid, { misc: ["vm_users"], vmid : vmid });
                if (apiResponse.status === 200) {
                    const data = await apiResponse.json();
                    console.log("VMSettingsUsers sourceUsers ::: " + JSON.stringify(data));
                    setSourceUsers(data?.misc?.users ?? []);
                }
            } catch (error) {
                console.error("Error fetching source users:", error);
            }
        }
        if (siteid) {
            fetchSourceUsers();
        }
    }, [siteid]);

    useEffect(() => {
        const processdrows = userList.map((user) => ({
            usersInfo: user,
            name: user?.name ?? "N/A",
            extension: user?.extension ?? "--",
            location_name: user?.location_name ?? "--",
            delete: (
                <>
                    <div className="d-flex align-items-center ms-2">
                        <div
                            className="custom-hover-bg p-1 cursor-pointer deleteToolTip"
                            onClick={(e) => handleDelete(e, user)}
                        >
                            <MDBIcon
                                fas
                                icon="times"
                                color="primary"
                                className="mx-2 fs-5"
                            />
                        </div>
                    </div>
                </>
            ),
        }));
        setVmUserDetails(processdrows);
        setFilteredRows(processdrows);
        setSearchValue("");
        setDataLoaded(true);
    }, [userList]);

    const fixedData = {
        columns: [
            { label: "Name", field: "name", sort: true },
            { label: "Extension", field: "extension" },
            // { label: "Location", field: "location_name" },
            { label: "Remove", field: "delete", sort: false },
        ],
        rows: filteredRows,
    };

    const handleManualSearch = (value) => {
        setSearchValue(value);
        const filtered = customSearch(vmUserDetails, value, UIConstant.VM_SETTINGS_USERS);
        setFilteredRows(filtered);
    };

    const handleDelete = (event, infoObj) => {
        event.preventDefault();
        dTogglePopup();
        setUpdateUsers([infoObj]);
    };

    useEffect(() => {
        if (updateUsers.length > 0 && isAddedUsers) {
            updateVMUser("grant");
            setIsAddedUsers(false);
        }
    }, [updateUsers, isAddedUsers]);

    useEffect(() => {
        if (updateUsers.length > 0 && isdeleteUser) {
            updateVMUser("revoke");
            setIsDeleteUser(false);
        }
    }, [updateUsers, isdeleteUser]);

    async function updateVMUser(action) {
        setInternalError(false);
        const agentids = updateUsers.map((item) => item.agentid);
        const payload = {
            reqid: 128,
            vmid: vmid,
            siteid: siteid,
            ...(action === "grant"
                ? { grant_vm_users: agentids }
                : { revoke_vm_users: agentids }),
        };
        console.log("VM Add/Remove Users Request ::: " + JSON.stringify(payload));

        const saverep = await updateSharedVoicemail(payload);
        const saverepeMsg = await saverep.json();
        const usernames = updateUsers.map((item) => item.name).join(", ");

        if (saverepeMsg.status && saverepeMsg.status === "success") {
            if (action === "grant") {
                setUserList((prev) => [...prev, ...updateUsers]);
                setSuccessMessage(`VM User(s) ${usernames} added successfully.`);
            } else {
                const agentidSet = new Set(agentids);
                setUserList((prev) => prev.filter((item) => !agentidSet.has(item.agentid)));
                setSuccessMessage(`VM User(s) ${usernames} removed successfully.`);
            }
        } else {
            if (action === "revoke") {
                // Restore removed users on failure
                const agentidSet = new Set(agentids);
                setUserList((prev) => {
                    const remaining = prev.filter((item) => !agentidSet.has(item.agentid));
                    return [...remaining, ...updateUsers];
                });
                setInternalError(true);
            }
        }
    }

    return (
        <>
            <div className="mb-3 general-settings m-0 ps-0">
                {t("users")}
            </div>
            <div className="smsgroupsdetails-datatable">
                <div className="actionbar">
                    <MDBBtn className="ms-2" onClick={toggleOpen}>
                        <MDBIcon fas icon="plus" className="me-2" />
                        {t("adduser")}
                    </MDBBtn>
                </div>

                <div className='mb-2 mt-0 position-relative w-auto'>
                    <MDBInput
                        label="Search"
                        ref={searchRef}
                        value={searchValue}
                        onChange={(e) => handleManualSearch(e.target.value)}
                    />
                    <MDBIcon fas icon="search" className='MDBDatatable-Search' />
                </div>
                <MDBDatatable
                    key={searchValue}
                    data={fixedData}
                    sortField="name"
                    fixedHeader
                    entries={50}
                    className="actioncolumn"
                />
            </div>

            {addUser && (
                <PopupLoaderNew
                    title="Add Users"
                    content={
                        <AddToUsers
                            onClose={toggleOpen}
                            selectedUsers={userList}
                            setUpdateUsers={setUpdateUsers}
                            setIsAddedUsers={setIsAddedUsers}
                            sourceUsers={sourceUsers}
                            ref={addUserRef}
                            setIsloading={setIsLoading}
                        />
                    }
                    onShow={setAddUser}
                    onClose={toggleOpen}
                    onSave={handleClick}
                    isloading={isLoading}
                    flag={1}
                />
            )}

            {isdelete && (
                <PopupLoaderNew
                    title="Remove User"
                    content={
                        <RemoveToUser
                            setIsDeleteUser={setIsDeleteUser}
                            onClose={dTogglePopup}
                            ref={addUserRef}
                            setIsloading={setIsLoading}
                        />
                    }
                    onShow={setIsDelete}
                    onClose={dTogglePopup}
                    onSave={handleClick}
                    isloading={isLoading}
                    flag={4}
                />
            )}

            {successMessage && (
                <TopSuccess>
                    <TopMessage successMessage={successMessage} />
                </TopSuccess>
            )}

            {internalError && (
                <MDBAlert
                    open
                    className="alert-center"
                    color="danger"
                    autohide
                    position="bottom-right"
                    delay={5000}
                    onClose={() => setInternalError(false)}
                >
                    Internal Server Error
                </MDBAlert>
            )}
        </>
    );
}

export default VMSettingsUsers;
